import React, { useCallback, useEffect, useMemo, useState } from "react";
import { InputField, DataTable, DropDownInput, Button } from "@/components";
import ColumnHelper from "@/utils/ColumnHelper";
import { useFilteredData } from "@/";
import { useLocation, useNavigate } from "react-router-dom";
import { SpaceWrapper } from "../../components";
import { StatesOptions, statesOptionsArray } from "../../utils/Data";
import {
  selectAllCustomers,
  fetchCustomerData,
  customerToggle,
} from "../../app/Slices";
import { useSelector, useDispatch } from "react-redux";
import { useAlert, useConfirmationDialog } from "@/";
import { RiFileExcel2Line } from "react-icons/ri";
const Customer = () => {
  const [resetPageCounter, setResetPageCounter] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { alert, AlertDialog } = useAlert();
  const { confirm, ConfirmationModal } = useConfirmationDialog();

  const [filters, setFilters] = useState({
    customerName: "",
    customerID: "",
    firmName: "",
    pincode: "",
    city: "",
    state: "",
    status: "",
    phone: "",
    email: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const filterConfig = useMemo(
    () => ({
      customerName: { type: "partial", itemKey: "name" },
      customerID: { type: "partial", itemKey: "clientId" },
      firmName: { type: "exact", itemKey: "kyc.businessDetails.firmName" },
      city: { type: "partial", itemKey: "address.city" },
      state: { type: "exact", itemKey: "address.state" },
      status: { type: "exact", itemKey: "kycStatus" },
      phone: { type: "partial", itemKey: "phones.phoneNumber" },
      email: { type: "partial", itemKey: "email" },
    }),
    []
  );
  const dispatch = useDispatch();
  const fetchData = async () => {
    const response = await dispatch(fetchCustomerData());
    // console.log("response::::::", response);
  };
  useEffect(() => {
    fetchData();
  }, [dispatch]);

  const customers = useSelector(selectAllCustomers) || [];
  const customerLoading = useSelector((state) => state.customer.loading);

  const { data: filteredData, isLoading } = useFilteredData(
    customers,
    appliedFilters,
    filterConfig
  );
  const isCustomersLoading = customerLoading || isLoading;
  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApply = useCallback(() => {
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  }, [filters]);

  const handleReset = useCallback(() => {
    const initialFilters = {
      customerName: "",
      customerID: "",
      firmName: "",
      city: "",
      state: "",
      status: "",
      phone: "",
      email: "",
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  }, []);

  const handleView = (id) => {
    navigate("/customer/details/" + id);
    console.log("Customer-ID:::", id);
  };

  const handleBlock = async (userId) => {
    if (!userId) {
      await alert("User ID is required to block a customer.");
      return;
    }

    await confirm("Are you sure you want to block this customer?");
    try {
      const response = await dispatch(
        customerToggle({ userId: userId, isActive: false })
      );

      if (customerToggle.fulfilled.match(response)) {
        await alert("Customer blocked successfully.");
      } else {
        console.error("Failed to block customer", response.payload);
        await alert(response.payload || "Failed to block customer.");
      }
    } catch (error) {
      console.error("Error blocking customer:", error);
      await alert("An error occurred while blocking the customer.");
    }
  };

  const handleUnblock = async (userId) => {
    if (!userId) {
      await alert("User ID is required to Unblocked a customer.");
      return;
    }

    await confirm("Are you sure you want to unblock this customer?");
    try {
      const response = await dispatch(
        customerToggle({ userId: userId, isActive: true })
      );

      if (customerToggle.fulfilled.match(response)) {
        await alert("Customer Unblocked successfully.");
      } else {
        console.error("Failed to Unblocked customer", response.payload);
        await alert(response.payload || "Failed to Unblocked customer.");
      }
    } catch (error) {
      console.error("Error Unblocked customer:", error);
      await alert("An error occurred while Unblocked the customer.");
    }
  };

  const statusStyle = {
    not_submitted: "bg-blue-100/90 text-blue-800",
    pending: "bg-yellow-100/90 text-yellow-800",
    approved: "bg-green-100/90 text-green-800",
    rejected: "bg-red-100/90 text-red-800",
  };

  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Customer Name",
        accessorKey: "name",
        meta: {
          tooltip: (row) => row.name || "N/A",
        },
      }),
      ColumnHelper({
        header: "Customer ID",
        accessorKey: "clientId",
        meta: {
          tooltip: (row) => row.clientId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Mobile No",
        accessorKey: "",
        cell: (info) => info.row.original.phones?.[0]?.phoneNumber || "N/A",

        meta: {
          tooltip: (row) => row.phones?.[0]?.phoneNumber || "N/A",
        },
      }),
      ColumnHelper({
        header: "Email",
        accessorKey: "",
        cell: (info) => info.row.original.email || "N/A",

        meta: {
          tooltip: (row) => row.email || "N/A",
        },
      }),
      ColumnHelper({
        header: "Firm Name",
        accessorKey: "kyc",
        cell: (info) => info.row.original?.firmName || "N/A",
        meta: {
          tooltip: (row) => row?.firmName || "N/A",
        },
      }),
      ColumnHelper({
        header: "City",
        accessorKey: "",
        cell: (info) => info.row.original.address?.city || "N/A",
        meta: {
          tooltip: (row) => row.address?.city || "N/A",
        },
      }),

      ColumnHelper({
        header: "State",
        accessorKey: "",
        cell: (info) =>
          StatesOptions[info.row.original.address?.state] || "N/A",
        meta: {
          tooltip: (row) => StatesOptions[row.address?.state] || "N/A",
        },
      }),
      ColumnHelper({
        header: "KYC Status ",
        accessorKey: "kycStatus",
        cell: (info) => (
          <span
            className={`px-2 py-1 rounded-md text-xs font-medium leading-3 ${
              statusStyle[info.row.original.kycStatus]
            }`}
          >
            {info.row.original.kycStatus}
          </span>
        ),
        meta: {
          tooltip: (row) => row.kycStatus || "N/A",
        },
      }),
      ColumnHelper({
        header: "Action",
        accessorKey: "",
        cell: (info) => (
          <div className="flex gap-1">
            {info.row.original.kycStatus !== "not_submitted" && (
              <Button
                size="xs"
                onClick={() => handleView(info.row.original.id)}
              >
                View
              </Button>
            )}
            {info.row.original.isActive ? (
              <Button
                size="xs"
                variant="outline"
                onClick={() => handleBlock(info.row.original._id)}
              >
                Block
              </Button>
            ) : (
              <Button
                size="xs"
                variant="outline"
                onClick={() => handleUnblock(info.row.original._id)}
              >
                Unblock
              </Button>
            )}
          </div>
        ),
        meta: {
          tooltip: () => "View or block customer",
        },
      }),
    ],
    []
  );

  return (
    <SpaceWrapper>
      <div className="w-full flex-1 flex flex-col gap-4 p-4 pb-20 md:pb-4 relative max-w-[2000px]">
        <h1 className="text-headline4 lg:text-headline3 font-nunito text-secondary/95 font-bold">
          Customers
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4">
          <InputField
            label="Customer Name"
            placeholder="Enter Customer Name"
            value={filters.customerName}
            onChange={(value) => handleChange("customerName", value)}
          />
          <InputField
            label="Customer ID"
            placeholder="Enter Customer ID"
            value={filters.customerID}
            onChange={(value) => handleChange("customerID", value)}
          />
          <InputField
            label="Firm Name"
            placeholder="Enter Firm Name"
            value={filters.firmName}
            onChange={(value) => handleChange("firmName", value)}
          />
          <InputField
            label="City"
            placeholder="Enter City"
            value={filters.city}
            onChange={(value) => handleChange("city", value)}
          />
          <DropDownInput
            label="State"
            placeholder="Select State"
            options={statesOptionsArray}
            value={filters.state}
            onChange={(value) => handleChange("state", value)}
          />
          <DropDownInput
            label="Status"
            placeholder="Select Status"
            options={[
              { value: "not_submitted", label: "Not Submitted" },
              { value: "pending", label: "Pending" },
              { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" },
            ]}
            value={filters.status}
            onChange={(value) => handleChange("status", value)}
          />
          <InputField
            label="Phone"
            placeholder="Enter Phone"
            value={filters.phone}
            onChange={(value) => handleChange("phone", value)}
          />
          <InputField
            label="Email"
            placeholder="Enter Email"
            value={filters.email}
            onChange={(value) => handleChange("email", value)}
          />
          <div className="flex flex-row gap-2 justify-start items-end">
            <Button size="md" onClick={handleApply} className="h-12">
              Apply
            </Button>
            <Button
              variant="outline"
              size="md"
              className="h-12"
              onClick={handleReset}
            >
              Reset
            </Button>
          </div>
        </div>
        <DataTable
          columns={columns}
          tittle="Customers"
          data={filteredData}
          isLoading={isCustomersLoading}
          resetPage={resetPageCounter}
          showCheckbox={false}
          actions={[
            {
              name: "Export All",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "Admin COD MasterFile",
                filename: `Customer_${new Date().getTime()}`,
              },
            },
          ]}
        />
      </div>
      <AlertDialog />
      <ConfirmationModal />
    </SpaceWrapper>
  );
};

export default Customer;
